import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../../api/apiService";
import { useParams, useLocation } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import Select from "react-select";
import { getBadgeClass } from "../../../config/utils";
import AdminUpdateAttendance from "../../../components/Modal/AdminUpdateAttendance";
import AdminUpdateChecktime from "../../../components/Modal/AdminUpdateChecktime";

const EmployeeAttendanceReport = () => {
  const { employeeId: alias } = useParams();
  const location = useLocation();
  const role = localStorage.getItem("role");

  const queryParams = new URLSearchParams(location.search);
  const monthParam = queryParams.get("month");
  const yearParam = queryParams.get("year");

  const [employeeId, setEmployeeId] = useState(alias || "");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);

  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(
    monthParam || (currentDate.getMonth() + 1).toString()
  );
  const [selectedYear, setSelectedYear] = useState(
    yearParam || currentDate.getFullYear().toString()
  );
  const [data, setData] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [checktimeModal, setChecktimeModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  // Fetch employees for the dropdown on component mount
  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get("/get_employees");
        setEmployees(response.data.data);
      } catch (error) {
        console.error("Error fetching employees:", error);
        toast.error("Failed to fetch employees");
      }
    };

    fetchEmployees();
  }, []);

  // Fetch attendance report when employeeId, selectedMonth, or selectedYear changes
  useEffect(() => {
    if (employeeId) {
      fetchAttendanceReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, selectedMonth, selectedYear]);

  const handleEmployeeChange = (selectedOption) => {
    setEmployeeId(selectedOption ? selectedOption.value : "");
  };

  const employeeOptions = employees.map((emp) => ({
    value: emp.employee_id,
    label: `${emp.first_name} ${emp.last_name}`,
  }));

  // Fetch attendance report based on employee ID, month, and year
  const fetchAttendanceReport = async (e) => {
    if (e) e.preventDefault();
    if (!employeeId) {
      toast.error("Please select a valid employee");
      return;
    }

    setIsLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await api.get(
        `employee/report-json/${employeeId}?month=${selectedMonth}&year=${selectedYear}`
      );

      // Assuming the controller returns the JSON structure as previously defined
      setData(response.data);
    } catch (error) {
      console.error("Error fetching attendance report:", error);
      toast.error("Failed to fetch attendance report");
      setError("Failed to fetch attendance report.");
    } finally {
      setIsLoading(false);
    }
  };

  const { employee, stats, total_hours, attendance_records } = data || {};

  const openModal = (employee) => {
    if (role === "admin") {
      setSelectedEmployee(employee);
      setModalShow(true);
    }
  };

  const openChecktimeModal = (employee, type) => {
    if (role === "admin") {
      setSelectedEmployee({ ...employee, check_type: type });
      setChecktimeModal(true);
    }
  };

  const handlePrintPage = (id) => {
    if (!id || !selectedMonth || !selectedYear) {
      toast.error("Invalid parameters for printing");
      return;
    }

    const printUrl = `/user/employee-report/print?id=${id}&month=${selectedMonth}&year=${selectedYear}`;
    window.open(printUrl, "_blank");
  };

  return (
    <main id="main" className="main">
      <div className="pagetitle">
        <h1>Employee Details</h1>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">
                  Select an employee to view their attendance details.
                </h5>

                {/* Employee Dropdown */}
                <form onSubmit={fetchAttendanceReport} className="row g-2">
                  <div className="col-md-3">
                    <Select
                      className="basic-single"
                      classNamePrefix="select"
                      value={
                        employeeOptions.find(
                          (option) => option.value === employeeId
                        ) || null
                      }
                      onChange={handleEmployeeChange}
                      options={employeeOptions}
                      placeholder="Search employee..."
                      isSearchable
                      isClearable
                      noOptionsMessage={() => "No options"}
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          borderRadius: "6px",
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          whiteSpace: "nowrap",
                        }),
                      }}
                    />
                  </div>

                  <div className="col-md-2">
                    <select
                      id="month"
                      className="form-select form-select-md"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      <option value="">Select Month</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {new Date(0, i).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2">
                    <select
                      id="year"
                      className="form-select form-select-md"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="">Select Year</option>
                      {Array.from({ length: 10 }, (_, i) => {
                        const year = currentDate.getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>

                  <div className="col-md-5">
                    <button type="submit" className="btn btn-primary">
                      Get Report
                    </button>
                    <button
                      className="btn btn-primary float-end"
                      onClick={() => handlePrintPage(employeeId)}
                      data-bs-toggle="tooltip"
                      data-bs-placement="top"
                      title="Print Report"
                    >
                      Print Report
                    </button>
                  </div>
                </form>

                {isLoading && (
                  <div className="text-center mt-3">
                    <Spinner animation="border" variant="primary" />
                    <span className="ms-2">Loading...</span>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}

                {/* Attendance Report */}
                {data && (
                  <div className="mt-4">
                    <h2>Employee Attendance Report</h2>
                    <p>
                      <strong>Present:</strong> {stats.present} |{" "}
                      <strong>Absent:</strong> {stats.absent}
                      {total_hours > 0 && (
                        <>
                          {" "}
                          |{" "}
                          <span>
                            <strong>Total Hours:</strong> {total_hours} hours
                          </span>
                        </>
                      )}
                    </p>
                    {employee?.shift && (
                      <p>
                        <strong>Current Assigned Shift:</strong>{" "}
                        {employee.shift.shift_name} |{" "}
                        <strong>Shift Start Time:</strong>{" "}
                        {employee.shift.start_time} |{" "}
                        <strong>Shift End Time:</strong>{" "}
                        {employee.shift.end_time}
                      </p>
                    )}
                    <div className="table-responsive">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Check In</th>
                            <th>Check Out</th>
                            <th>Total Hours</th>
                            <th>Check In</th>
                            <th>Check Out</th>
                            <th>Approved</th>
                            {/* <th>Created By</th> */}
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendance_records.length === 0 ? (
                            <tr>
                              <td colSpan="9" className="text-center">
                                No attendance records found.
                              </td>
                            </tr>
                          ) : (
                            attendance_records.map((record, index) => (
                              <tr key={index}>
                                <td>{record.date}</td>
                                <td>
                                  <span
                                    onClick={
                                      role === "admin"
                                        ? () =>
                                            openChecktimeModal(
                                              record,
                                              "Check In"
                                            )
                                        : undefined
                                    }
                                    className={`d-flex align-items-center gap-1 c-pointer ${
                                      record.check_in && role === "admin"
                                        ? "text-primary"
                                        : ""
                                    }`}
                                  >
                                    {record.check_in || "--"}
                                    {role === "admin" && (
                                      <i className="bi bi-pencil font-12"></i>
                                    )}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    onClick={
                                      record.check_in && role === "admin"
                                        ? () =>
                                            openChecktimeModal(
                                              record,
                                              "Check Out"
                                            )
                                        : undefined
                                    }
                                    className={`d-flex align-items-center gap-1 ${
                                      record.check_in ? "c-pointer" : "c-auto"
                                    } ${
                                      record.check_out && role === "admin"
                                        ? "text-primary"
                                        : ""
                                    }`}
                                  >
                                    {record.check_out || "--"}
                                    {record.check_in && role === "admin" && (
                                      <i className="bi bi-pencil font-12"></i>
                                    )}
                                  </span>
                                </td>
                                <td>{record.total_hours} hours</td>
                                <td>
                                  <span
                                    className={`badge bg-${getBadgeClass(
                                      record.check_in_status
                                    )}`}
                                    style={{
                                      cursor:
                                        role === "admin" ? "pointer" : "auto",
                                    }}
                                    onClick={() => openModal(record)}
                                  >
                                    {record.check_in_status}
                                  </span>
                                </td>
                                <td>
                                  {record.check_out_status ? (
                                    <span
                                      className={`badge bg-${getBadgeClass(
                                        record.check_out_status
                                      )}`}
                                    >
                                      {record.check_out_status}
                                    </span>
                                  ) : (
                                    "--"
                                  )}
                                </td>
                                <td>
                                  <span
                                    className={`badge bg-${
                                      record.approved ? "success" : "danger"
                                    }`}
                                  >
                                    {record.approved ? "Yes" : "No"}
                                  </span>
                                </td>
                                {/* <td>
                                 <small className="text-muted"> {record.created_by || "--"}</small>
                                  </td> */}
                                <td>
                                  <small className="text-muted">
                                    {record.remarks || "--"}
                                  </small>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <AdminUpdateAttendance
        show={modalShow}
        onClose={() => setModalShow(false)}
        employee={selectedEmployee}
        employeeId={employeeId}
        onAttendanceUpdated={fetchAttendanceReport}
      />
      <AdminUpdateChecktime
        show={checktimeModal}
        onClose={() => setChecktimeModal(false)}
        currentEmployee={selectedEmployee}
        employeeId={employeeId}
        onAttendanceUpdated={fetchAttendanceReport}
      />
    </main>
  );
};

export default EmployeeAttendanceReport;
